import type { Colega } from "../types";

/**
 * Almikia Epic E2b (TASK-022): pure helpers behind the "Colegas" screen
 * (external brokers who refer leads). Phone rule mirrors the SQL/FakeRest
 * guard: exactly 10 digits, with spaces and dashes stripped first.
 */

export const COLEGA_NAME_REQUIRED = "Escribe el nombre completo";
export const COLEGA_PHONE_INVALID = "El teléfono debe tener 10 dígitos";
export const COLEGA_BROKERAGE_REQUIRED =
  "Escribe la inmobiliaria o elige Independiente";
export const COLEGA_EMAIL_INVALID = "Escribe un correo válido";
export const COLEGA_GENERIC_ERROR =
  "No se pudo guardar el cambio. Inténtalo de nuevo.";

/** Stored value of `colegas.brokerage` for a colega with no inmobiliaria. */
export const INDEPENDENT_BROKERAGE = "independiente";

const PHONE_PATTERN = /^[0-9]{10}$/;
const EMAIL_PATTERN = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

export interface ColegaFormValues {
  full_name: string;
  phone: string;
  isIndependent: boolean;
  brokerage: string;
  email: string;
  notes: string;
}

export interface ColegaFormErrors {
  full_name?: string;
  phone?: string;
  brokerage?: string;
  email?: string;
}

/** Strips spaces and dashes so "55 1234-5678" validates as 5512345678. */
export const normalizePhone = (raw: string): string =>
  raw.replace(/[\s-]/g, "");

/** Validates the colega form; an empty object means valid. */
export function validateColegaForm(values: ColegaFormValues): ColegaFormErrors {
  const errors: ColegaFormErrors = {};
  if (values.full_name.trim().length === 0) {
    errors.full_name = COLEGA_NAME_REQUIRED;
  }
  if (!PHONE_PATTERN.test(normalizePhone(values.phone))) {
    errors.phone = COLEGA_PHONE_INVALID;
  }
  if (!values.isIndependent && values.brokerage.trim().length === 0) {
    errors.brokerage = COLEGA_BROKERAGE_REQUIRED;
  }
  const email = values.email.trim();
  if (email.length > 0 && !EMAIL_PATTERN.test(email)) {
    errors.email = COLEGA_EMAIL_INVALID;
  }
  return errors;
}

/** The record sent to the data provider (normalised phone, 'independiente' literal). */
export function buildColegaPayload(
  values: ColegaFormValues,
): Pick<Colega, "full_name" | "phone" | "brokerage" | "email" | "notes"> {
  const email = values.email.trim();
  const notes = values.notes.trim();
  return {
    full_name: values.full_name.trim(),
    phone: normalizePhone(values.phone),
    brokerage: values.isIndependent
      ? INDEPENDENT_BROKERAGE
      : values.brokerage.trim(),
    email: email.length > 0 ? email : null,
    notes: notes.length > 0 ? notes : null,
  };
}

/** Initial form values for an existing colega, or blanks for a new one. */
export function toColegaFormValues(colega?: Colega): ColegaFormValues {
  const isIndependent =
    colega != null && colega.brokerage === INDEPENDENT_BROKERAGE;
  return {
    full_name: colega?.full_name ?? "",
    phone: colega?.phone ?? "",
    isIndependent,
    brokerage: colega == null || isIndependent ? "" : colega.brokerage,
    email: colega?.email ?? "",
    notes: colega?.notes ?? "",
  };
}

const fold = (text: string): string =>
  text.normalize("NFD").replace(/[̀-ͯ]/g, "").toLowerCase();

/**
 * Search by name, phone or inmobiliaria (case/accent-insensitive; a phone
 * query ignores spaces and dashes). Active colegas first, then by name.
 */
export function filterColegas(
  colegas: readonly Colega[],
  query: string,
): Colega[] {
  const text = fold(query.trim());
  const digits = normalizePhone(query.trim());
  const matches =
    text.length === 0
      ? colegas
      : colegas.filter(
          (colega) =>
            fold(colega.full_name).includes(text) ||
            fold(colega.brokerage).includes(text) ||
            (digits.length > 0 && colega.phone.includes(digits)),
        );
  return matches.slice().sort((a, b) => {
    if (a.active !== b.active) return a.active ? -1 : 1;
    return a.full_name.localeCompare(b.full_name, "es");
  });
}

/** Server rejections are already Spanish sentences; pass them through. */
export function toColegaErrorMessage(error: unknown): string {
  const message = error instanceof Error ? error.message : undefined;
  return message || COLEGA_GENERIC_ERROR;
}
